// LockedCursorTag — 再接続中、カーソル付近に出す黄色い小札「つなぎなおし中…」
// (docs/_wip/reconnect-draw-lock-2026-10-12.md「追記: 目立たせる」)。
//
// - PC (pointerType mouse/pen): ロック中に隠している BrushCursorOverlay の代わりに、
//   キャンバス上の hover に右下で追従。キャンバス外に出たら消す。端では見切れないよう寄せる。
// - タッチ: ロック中のタップ (`tapKey` 増加) で、直近の pointerdown 位置に約 1.5 秒。
// - 位置は client 座標 → ステージ枠 (親) の rect 相対で決めるので、ズーム / パン / 回転に
//   依らずずれない。自分の画面だけ。aria-hidden (読み上げは ReconnectOverlay の live region)。
//   名表示 (NameLabelOverlay) と同じ「キャンバス上の小さな札」の流儀。

import { useCallback, useEffect, useRef, useState } from 'react';

import { useConnectionLocked } from '../providers/ConnectionLockContext.js';

export const TOUCH_TAG_MS = 1500;
const OFFSET_PX = 14;
const MARGIN_PX = 4;
const FALLBACK_SIZE = { w: 120, h: 24 };

export interface TagPlacement {
  readonly left: number;
  readonly top: number;
}

/** ポインタ (枠相対 px) の右下に置き、枠からはみ出すなら反対側へ寄せる。 */
export const placeTag = (
  px: number,
  py: number,
  tag: { w: number; h: number },
  box: { w: number; h: number },
): TagPlacement => {
  let left = px + OFFSET_PX;
  let top = py + OFFSET_PX;
  if (left + tag.w > box.w - MARGIN_PX) left = px - OFFSET_PX - tag.w;
  if (top + tag.h > box.h - MARGIN_PX) top = py - OFFSET_PX - tag.h;
  return {
    left: Math.max(MARGIN_PX, Math.min(left, box.w - tag.w - MARGIN_PX)),
    top: Math.max(MARGIN_PX, Math.min(top, box.h - tag.h - MARGIN_PX)),
  };
};

export interface LockedCursorTagProps {
  readonly tapKey: number;
}

export const LockedCursorTag = ({ tapKey }: LockedCursorTagProps): React.ReactNode => {
  const locked = useConnectionLocked();
  const holderRef = useRef<HTMLDivElement | null>(null);
  const tagRef = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<TagPlacement | null>(null);
  const lastDownRef = useRef<{ x: number; y: number; touch: boolean } | null>(null);

  const placeAt = useCallback((clientX: number, clientY: number): void => {
    const parent = holderRef.current?.parentElement;
    if (!parent) return;
    const r = parent.getBoundingClientRect();
    const tag = tagRef.current
      ? {
          w: tagRef.current.offsetWidth || FALLBACK_SIZE.w,
          h: tagRef.current.offsetHeight || FALLBACK_SIZE.h,
        }
      : FALLBACK_SIZE;
    setPlace(placeTag(clientX - r.left, clientY - r.top, tag, { w: r.width, h: r.height }));
  }, []);

  // PC: hover 追従 + 直近 pointerdown の記録 (タッチのタップ位置用)。親 (ステージ枠) に付ける。
  useEffect(() => {
    const parent = holderRef.current?.parentElement;
    if (!parent || !locked) {
      setPlace(null);
      return;
    }
    const onMove = (e: PointerEvent): void => {
      if (e.pointerType === 'touch') return;
      const onCanvas =
        e.target instanceof Element && e.target.closest('canvas[data-oekaki-canvas]');
      if (!onCanvas) {
        setPlace(null);
        return;
      }
      placeAt(e.clientX, e.clientY);
    };
    const onDown = (e: PointerEvent): void => {
      lastDownRef.current = { x: e.clientX, y: e.clientY, touch: e.pointerType === 'touch' };
    };
    // タッチは指を離すたびに pointerleave が来る (= タップ直後の札を消してしまう) ので無視する。
    const onLeave = (e: PointerEvent): void => {
      if (e.pointerType !== 'touch') setPlace(null);
    };
    parent.addEventListener('pointermove', onMove);
    parent.addEventListener('pointerdown', onDown, true);
    parent.addEventListener('pointerleave', onLeave);
    return () => {
      parent.removeEventListener('pointermove', onMove);
      parent.removeEventListener('pointerdown', onDown, true);
      parent.removeEventListener('pointerleave', onLeave);
      setPlace(null);
    };
    // placeAt は ref のみ参照 (安定)。
  }, [locked, placeAt]);

  // タッチ: ロック中のタップ合図 (tapKey 増加) で約 1.5 秒表示。
  const baseKeyRef = useRef(tapKey);
  useEffect(() => {
    if (!locked) {
      baseKeyRef.current = tapKey;
      return;
    }
    if (tapKey === baseKeyRef.current) return;
    baseKeyRef.current = tapKey;
    const down = lastDownRef.current;
    if (!down?.touch) return;
    placeAt(down.x, down.y);
    const timer = setTimeout(() => setPlace(null), TOUCH_TAG_MS);
    return () => clearTimeout(timer);
  }, [tapKey, locked, placeAt]);

  return (
    <div ref={holderRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-20">
      {locked && place !== null && (
        <div
          ref={tagRef}
          data-oekaki-locked-tag
          className="oekaki-locked-tag absolute whitespace-nowrap rounded px-2 py-0.5 text-xs font-bold"
          style={{ left: place.left, top: place.top }}
        >
          つなぎなおし中…
        </div>
      )}
    </div>
  );
};
